/**
 * KeyValuePairsComponent
 * Manages collection of key-value pairs
 */

import React, { useCallback, useMemo, useState, memo } from 'react';
import type { FormControlProps } from '../../types';

export interface KeyValuePair {
  key: string;
  value: string;
}

interface KeyValuePairsComponentProps extends FormControlProps {
  pairs?: KeyValuePair[];
  keyPlaceholder?: string;
  valuePlaceholder?: string;
  allowDuplicateKeys?: boolean;
  maxPairs?: number;
  onChange?: (pairs: KeyValuePair[]) => void;
}

export const KeyValuePairsComponent: React.FC<KeyValuePairsComponentProps> = memo(({
  value = [],
  pairs = [],
  keyPlaceholder = 'key',
  valuePlaceholder = 'value',
  allowDuplicateKeys = false,
  maxPairs = 0,
  onChange,
  disabled = false,
  required = false,
  label,
  error,
  helpText,
}) => {
  const inputId = useMemo(
    () => `kvpairs-${label?.replace(/\s+/g, '-').toLowerCase()}`,
    [label],
  );

  const [pairsList, setPairsList] = useState<KeyValuePair[]>(
    Array.isArray(value) && value.length > 0 ? (value as KeyValuePair[]) : pairs,
  );

  const handleUpdatePair = useCallback(
    (index: number, field: 'key' | 'value', val: string) => {
      const updated = pairsList.map((p, i) =>
        i === index ? { ...p, [field]: val } : p,
      );
      setPairsList(updated);
      onChange?.(updated);
    },
    [pairsList, onChange],
  );

  const handleAddPair = useCallback(() => {
    if (maxPairs > 0 && pairsList.length >= maxPairs) return;

    const newPairs = [...pairsList, { key: '', value: '' }];
    setPairsList(newPairs);
    onChange?.(newPairs);
  }, [pairsList, maxPairs, onChange]);

  const handleRemovePair = useCallback(
    (index: number) => {
      const newPairs = pairsList.filter((_, i) => i !== index);
      setPairsList(newPairs);
      onChange?.(newPairs);
    },
    [pairsList, onChange],
  );

  const isDuplicateKey = (key: string, currentIndex: number): boolean => {
    if (allowDuplicateKeys || !key) return false;
    return pairsList.some((p, i) => i !== currentIndex && p.key === key);
  };

  const canAddMore = maxPairs === 0 || pairsList.length < maxPairs;

  return (
    <div className="af-form-field__group kvpairs-group">
      {label && (
        <label className="af-form-field__label">
          {label}
          {required && <span className="af-form-field__required">*</span>}
        </label>
      )}

      <div className="kvpairs__container">
        <div className="kvpairs__header">
          <div className="kvpairs__header-key">Key</div>
          <div className="kvpairs__header-value">Value</div>
          <div className="kvpairs__header-actions">Actions</div>
        </div>

        {pairsList.length === 0 && (
          <p className="kvpairs__empty">No pairs added yet.</p>
        )}

        {pairsList.map((pair, index) => {
          const isDuplicate = isDuplicateKey(pair.key, index);

          return (
            <div
              key={index}
              className={`kvpairs__row ${isDuplicate ? 'kvpairs__row--error' : ''}`}
            >
              <input
                type="text"
                value={pair.key}
                onChange={(e) => handleUpdatePair(index, 'key', e.target.value)}
                placeholder={keyPlaceholder}
                disabled={disabled}
                className="kvpairs__input kvpairs__input--key"
                aria-label={`Pair ${index + 1} key`}
              />

              <input
                type="text"
                value={pair.value}
                onChange={(e) => handleUpdatePair(index, 'value', e.target.value)}
                placeholder={valuePlaceholder}
                disabled={disabled}
                className="kvpairs__input kvpairs__input--value"
                aria-label={`Pair ${index + 1} value`}
              />

              <button
                type="button"
                onClick={() => handleRemovePair(index)}
                disabled={disabled}
                className="kvpairs__remove-button"
                aria-label={`Remove pair ${index + 1}`}
              >
                Remove
              </button>

              {isDuplicate && (
                <span className="kvpairs__duplicate-warning" role="alert">
                  Duplicate key
                </span>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={handleAddPair}
          disabled={disabled || !canAddMore}
          className="kvpairs__add-button"
          aria-label="Add new pair"
        >
          Add Pair
        </button>
      </div>

      {maxPairs > 0 && (
        <p className="kvpairs__counter">
          {pairsList.length} / {maxPairs}
        </p>
      )}

      {error && (
        <div id={`${inputId}-error`} className="af-form-field__error">
          {error}
        </div>
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-form-field__help">
          {helpText}
        </div>
      )}
    </div>
  );
});
